import React from "react";
import { Tooltip } from "antd";
import { User, MapPin, Clock, Users, Coffee, DollarSign } from "react-feather";
import moment from "moment";

const safeText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const getKotCount = (table) => {
  if (typeof table?.kotCount === "number") return table.kotCount;
  if (Array.isArray(table?.kots)) return table.kots.length;
  return Number(table?.kots || 0) || 0;
};

const formatMoney = (value) => {
  const currency = localStorage.getItem("currencycode") || "₹";
  const n = Number(value || 0);
  return `${currency}${n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const shortText = (value, max = 24) => {
  const text = safeText(value);
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const getSinceLabel = (occupiedAt, orderStart) => {
  const start = occupiedAt || orderStart;
  if (!start) return null;

  const m = moment(start);
  if (!m.isValid()) return null;

  return moment.duration(moment().diff(m)).humanize();
};

const CapacityDots = ({ capacity }) => {
  const count = Math.max(1, Math.min(Number(capacity || 0), 10));

  return (
    <div className="ns-table-capacity-dots" aria-label={`${capacity || 0} seats`}>
      {Array.from({ length: count }).map((_, index) => (
        <span key={index}>
          <User size={12} />
        </span>
      ))}
    </div>
  );
};

const TableTooltip = ({ group, status, mergedId }) => {
  const safeGroup = Array.isArray(group) ? group.filter(Boolean) : [];
  const firstTable = safeGroup[0] || {};
  const totalCapacity = safeGroup.reduce((sum, table) => sum + Number(table.capacity || 0), 0);
  const totalGuests = safeGroup.reduce((sum, table) => sum + Number(table.guests || 0), 0);
  const totalKots = safeGroup.reduce((sum, table) => sum + getKotCount(table), 0);
  const totalBillAmount = safeGroup.reduce((sum, table) => sum + Number(table.billAmount || 0), 0);
  const server = firstTable.server || "Unassigned";
  const orderStart = safeGroup.find((table) => table.orderStart)?.orderStart;
  const occupiedAt = safeGroup.find((table) => table.occupied_at)?.occupied_at;
  const sinceLabel = getSinceLabel(occupiedAt, orderStart);
  const reservation = safeGroup.find((table) => table.reservations?.length > 0)?.reservations?.[0];
  const zone = safeText(firstTable.zone, "No zone");
  const title = mergedId ? `Merged ${mergedId}` : safeText(firstTable.name, "Table");

  const tooltipContent = (
    <div className="ns-table-tooltip-content">
      <div className="ns-table-tooltip-title">{title}</div>

      {mergedId ? (
        <div className="ns-table-tooltip-row">
          <strong>Tables</strong>
          <span>{safeGroup.map((table) => table.name).filter(Boolean).join(", ")}</span>
        </div>
      ) : null}

      <div className="ns-table-tooltip-row">
        <strong>Zone</strong>
        <span>{zone}</span>
      </div>

      <div className="ns-table-tooltip-row">
        <strong>Capacity</strong>
        <span>{totalCapacity}</span>
      </div>

      <div className="ns-table-tooltip-row">
        <strong>Status</strong>
        <span>{status || "Available"}</span>
      </div>

      {status !== "Available" && status !== "Dirty" ? (
        <>
          <div className="ns-table-tooltip-row">
            <strong>Guests</strong>
            <span>{totalGuests || "-"}</span>
          </div>
          <div className="ns-table-tooltip-row">
            <strong>Server</strong>
            <span>{server}</span>
          </div>
        </>
      ) : null}

      {sinceLabel ? (
        <div className="ns-table-tooltip-row">
          <strong>Since</strong>
          <span>{sinceLabel}</span>
        </div>
      ) : null}

      {totalKots > 0 ? (
        <>
          <div className="ns-table-tooltip-row">
            <strong>KOTs</strong>
            <span>{totalKots}</span>
          </div>
          <div className="ns-table-tooltip-row">
            <strong>Value</strong>
            <span>{formatMoney(totalBillAmount)}</span>
          </div>
        </>
      ) : null}

      {status === "Reserved" ? (
        <>
          <div className="ns-table-tooltip-row">
            <strong>Guest</strong>
            <span>{reservation?.guest_name || "Unknown"}</span>
          </div>
          <div className="ns-table-tooltip-row">
            <strong>Expected</strong>
            <span>
              {reservation?.reservation_time
                ? moment(reservation.reservation_time).format("HH:mm")
                : "-"}
            </span>
          </div>
          <div className="ns-table-tooltip-row">
            <strong>Covers</strong>
            <span>{reservation?.party_size || 0}</span>
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" overlayClassName="ns-table-tooltip">
      <div className="ns-table-tooltip-trigger">
        <div className="ns-table-title-row">
          <span className="ns-table-name" title={title}>
            {title}
          </span>
        </div>

        <div className="ns-table-zone">
          <MapPin size={13} />
          <span title={zone}>{shortText(zone, mergedId ? 42 : 22)}</span>
        </div>

        <div className="ns-table-mini-stats">
          <span>
            <Users size={13} />
            {totalCapacity}
          </span>
          <span>
            <Coffee size={13} />
            {totalKots}
          </span>
          {totalBillAmount > 0 ? (
            <span>
              <DollarSign size={13} />
              {formatMoney(totalBillAmount)}
            </span>
          ) : null}
        </div>

        <CapacityDots capacity={totalCapacity} />

        {sinceLabel ? (
          <div className="ns-table-since">
            <Clock size={13} />
            Since {sinceLabel}
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

export default TableTooltip;
